import { FormProvider } from 'forms';
import React, { useMemo } from 'react';
import { CreateClaimModal } from '../Create/CreateClaimModal';
import { useQuery } from 'react-query';
import { useSearchParams } from 'hooks/useSearchParams';
import { Spinner, useToast } from '@razorpay/blade/components';
import { generateFile } from '../utils';
import api from 'api';
import type {
  GetFlexiReimbursementClaimResponse,
  GetBatchSignedURLsResponse,
  SignedURLFileData,
  FlexiReimbursementClaim,
} from 'api/gql-utils/flexibleBenefits/types';
import type { BladeFile } from '@razorpay/blade/components';
import { paiseAmountToRupee } from 'utils/Numbers';

export const EditFlexiBenefitClaim = () => {
  const { urlParams } = useSearchParams();
  const toast = useToast();
  const claimId = urlParams.get('claimId');

  const getClaimData = useQuery<
    GetFlexiReimbursementClaimResponse,
    Error,
    FlexiReimbursementClaim | undefined
  >({
    queryKey: [api.flexibleBenefitComponents.queries.getFlexiReimbursementClaim, { claimId }],
    queryFn: () => {
      return api.flexibleBenefitComponents.queries.getFlexiReimbursementClaim({
        claim_id: claimId || '',
      });
    },
    select: (data) => data?.payrollGetFlexiReimbursementClaim?.claim,
    onError: (error: Error) => {
      toast.show({
        content: error.message || 'Error fetching claim data',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const getUFHFiles = useQuery<GetBatchSignedURLsResponse, Error, BladeFile[] | undefined>({
    queryKey: [
      api.flexibleBenefitComponents.queries.getClaimEvidenceFiles,
      { ids: getClaimData.data?.claim_evidence_id },
    ],
    queryFn: () => {
      return api.flexibleBenefitComponents.queries.getClaimEvidenceFiles({
        file_requests:
          getClaimData.data?.claim_evidence_id.map((id: string) => ({ file_id: id })) || [],
      });
    },
    enabled:
      !!getClaimData.data?.claim_evidence_id && getClaimData.data?.claim_evidence_id?.length > 0,
    select: (data) =>
      data?.payrollGetBatchSignedURLs?.data.map((file: SignedURLFileData) =>
        generateFile(
          file.size,
          `${file.name}.${file.extension}`,
          file.extension,
          file.signed_url,
          file.file_id,
        ),
      ),
    onError: (error: Error) => {
      toast.show({
        content: error.message || 'Error fetching claim evidence files',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const claimData = useMemo(() => {
    if (!getClaimData.data) return null;
    return {
      Component_id: getClaimData.data.component_id || '',
      Component_name: getClaimData.data.component_display_name || '',
      Bill_amount: paiseAmountToRupee(getClaimData.data.claim_amount || 0).toString() || '',
      Expense_date: Number(getClaimData.data.expense_date) * 1000 || '',
      Description: getClaimData.data.claim_description || '',
      Files: getUFHFiles.data || [],
      Claim_id: claimId || '',
      Claim_status: getClaimData.data.claim_status || '',
    };
  }, [getClaimData.data, getUFHFiles.data, claimId]);

  const isLoading = getClaimData.isLoading || getUFHFiles.isLoading;

  return isLoading ? (
    <Spinner accessibilityLabel="Loading" />
  ) : (
    <FormProvider initialState={claimData}>
      <CreateClaimModal isEditFlow />
    </FormProvider>
  );
};
